// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file SideNavItem.tsx
 * @input Uses React, ReactNode, StyleX, Icon, IconType
 * @output Exports SideNavItem component and SideNavItemProps
 * @position Core implementation; used inside SideNav children
 *
 * Navigation item with icon, selected state, and nesting.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/SideNav/SideNav.doc.mjs
 * - /packages/core/src/SideNav/SideNav.test.tsx
 * - /packages/core/src/SideNav/index.ts
 * - /apps/storybook/stories/SideNav.stories.tsx
 * - /packages/cli/templates/blocks/components/SideNav/ (showcase blocks)
 */
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, sizeVars, fontWeightVars, typeScaleVars, durationVars, easeVars, borderVars, radiusVars } from "../theme/tokens.stylex.js";
import { renderIconSlot } from "../Icon/index.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { usePopover } from "../Popover/usePopover.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { Tooltip } from "../Tooltip/index.js";
import { navItemStyles } from "../NavItem/navItemStyles.stylex.js";
import { useSideNavCollapse, SideNavCollapseContext } from "./SideNavCollapseContext.js";
import { getIcon } from "../Icon/globalIconRegistry.js";
import { useSideNavRenderMode } from "./SideNavRenderContext.js";
import { useAppShellMobile } from "../AppShell/AppShellMobileContext.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  itemCollapsed: {
    kjj79g: "xl56j7k",
    kzqmXN: "x805d0l",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  itemCollapsedSm: {
    kzqmXN: "x1fvqzum",
    $$css: true
  },
  itemCollapsedLg: {
    kzqmXN: "x1m747yf",
    $$css: true
  }
};

// Non-collapsed state for popover children — ensures nested items render expanded
const EXPANDED_COLLAPSE_STATE = {
  isCollapsed: false,
  toggle: () => {},
  isCollapsible: false
};

// =============================================================================
// NavItemElement — resolves link vs button based on props
// =============================================================================

/**
 * Renders `<a>` (via LinkComponent) when `href` is set, otherwise `<button>`.
 * Centralizes the link-vs-button decision used across all SideNavItem paths.
 */
function NavItemElement({
  href,
  as,
  isDisabled,
  onClick,
  ref,
  children,
  ...rest
}) {
  const LinkComponent = useLinkComponent(as);
  if (href && !isDisabled) {
    return /*#__PURE__*/_jsx(LinkComponent, {
      ref: ref,
      href: href,
      onClick: onClick,
      ...rest,
      children: children
    });
  }
  return /*#__PURE__*/_jsx("button", {
    ref: ref,
    type: "button",
    onClick: onClick,
    disabled: isDisabled,
    ...rest,
    children: children
  });
}

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * Navigation item for SideNav.
 *
 * Supports icons, selected state, nesting, and end content like badges or counts.
 *
 * @example
 * ```
 * <SideNavItem
 *   label="Dashboard"
 *   icon={HomeIcon}
 *   selectedIcon={HomeIconSolid}
 *   isSelected
 *   href="/dashboard"
 * />
 * <SideNavItem label="Settings" icon={CogIcon}>
 *   <SideNavItem label="General" href="/settings/general" />
 *   <SideNavItem label="Security" href="/settings/security" />
 * </SideNavItem>
 * ```
 */
export function SideNavItem({
  as,
  label,
  icon,
  selectedIcon,
  isSelected = false,
  isDisabled = false,
  href,
  onClick,
  endContent,
  children,
  collapsible: itemCollapsible,
  size = 'md',
  'data-testid': testId,
  ref
}) {
  const t = useTranslator();
  const {
    isCollapsed
  } = useSideNavCollapse();
  const renderMode = useSideNavRenderMode();
  const {
    closeMobileNav
  } = useAppShellMobile();
  const isInDrawer = renderMode === 'drawer' || renderMode === 'drawer-content';
  const id = useId();
  const hasChildren = !!children;
  const itemRef = useRef(null);

  // Popover for collapsed items with children
  const popover = usePopover({
    hasLightDismiss: true,
    hasAutoFocus: true,
    hasCloseButton: false,
    dialogLabel: `${label} submenu`
  });

  // Collapse state for items with children
  const itemCollapsibleConfig = useMemo(() => typeof itemCollapsible === 'object' ? itemCollapsible : {}, [itemCollapsible]);
  const isItemCollapsible = hasChildren && itemCollapsible !== false;
  const itemControlledCollapsed = itemCollapsibleConfig.isCollapsed;
  const isItemControlled = itemControlledCollapsed !== undefined;
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(itemCollapsibleConfig.defaultIsCollapsed ?? false);
  const isItemCollapsed = isItemControlled ? itemControlledCollapsed : uncontrolledCollapsed;
  const toggleItemCollapse = useCallback(() => {
    const next = !isItemCollapsed;
    if (!isItemControlled) {
      setUncontrolledCollapsed(next);
    }
    itemCollapsibleConfig.onCollapsedChange?.(next);
  }, [isItemCollapsed, isItemControlled, itemCollapsibleConfig]);
  const displayIcon = isSelected && selectedIcon ? selectedIcon : icon;

  // When collapsible + a primary action (href or onClick), the action and
  // toggle are independent: clicking the label navigates/fires onClick,
  // clicking the chevron expands/collapses children.
  const hasPrimaryAction = !!href || !!onClick;
  const hasIndependentToggle = isItemCollapsible && hasPrimaryAction && !isCollapsed;
  const handleClick = e => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    if (isItemCollapsible && !hasIndependentToggle && !isCollapsed) {
      e.preventDefault();
      toggleItemCollapse();
      return;
    }
    onClick?.(e);
    // Close the mobile nav when a nav item is activated inside the drawer
    if (isInDrawer) {
      closeMobileNav();
    }
  };
  const handleToggleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    toggleItemCollapse();
  };

  // Hover handlers for collapsed popover (mirrors TopNavMenu pattern)
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const clearPopoverTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);
  const schedulePopoverShow = useCallback(() => {
    clearPopoverTimeouts();
    showTimeoutRef.current = setTimeout(() => {
      popover.show({
        skipAutoFocus: true
      });
    }, 150);
  }, [clearPopoverTimeouts, popover]);
  const schedulePopoverHide = useCallback(() => {
    clearPopoverTimeouts();
    hideTimeoutRef.current = setTimeout(() => {
      popover.hide();
    }, 200);
  }, [clearPopoverTimeouts, popover]);
  const handlePopoverMouseEnter = useCallback(() => {
    schedulePopoverShow();
  }, [schedulePopoverShow]);
  const handlePopoverMouseLeave = useCallback(() => {
    schedulePopoverHide();
  }, [schedulePopoverHide]);

  // In collapsed mode: hide items without icons
  if (isCollapsed && !icon) {
    return null;
  }

  // =========================================================================
  // Collapsed mode — icon-only items, popover for items with children
  // =========================================================================
  if (isCollapsed) {
    const collapsedIcon = displayIcon && renderIconSlot(displayIcon, {
      size: 'sm',
      color: isSelected ? 'primary' : isDisabled ? 'disabled' : 'secondary'
    });

    // Shared collapsed item styles — used by trigger, link, and button
    const collapsedItemStyles = mergeProps(themeProps('side-nav-item', {
      size,
      selected: isSelected ? 'selected' : null
    }), stylex.props(navItemStyles.item, navItemStyles[size], styles.itemCollapsed, size === 'sm' && styles.itemCollapsedSm, size === 'lg' && styles.itemCollapsedLg, isSelected && navItemStyles.selected, isDisabled && navItemStyles.disabled));

    // Items with children: popover trigger + popover
    if (hasChildren) {
      return /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 xdt5ytf xh8yej3"
        },
        children: [/*#__PURE__*/_jsx("button", {
          ref: mergeRefs(ref, popover.triggerRef),
          type: "button",
          onClick: popover.toggle,
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave,
          "aria-label": label,
          "data-testid": testId,
          ...popover.triggerProps,
          ...collapsedItemStyles,
          children: collapsedIcon
        }), popover.render(/*#__PURE__*/_jsxs("div", {
          ...{
            className: "x1litavf x1y0btm7 x14i3s5s xu0wf1k x7a5moj x11g1kdw xfb3i0g"
          },
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave,
          onClick: () => popover.hide(),
          children: [/*#__PURE__*/_jsx("div", {
            ...{
              className: "xf314gf xu0wf1k x141an7d x2mo6ok xv1l7n4 x1ltkj2j"
            },
            children: label
          }), /*#__PURE__*/_jsx(SideNavCollapseContext, {
            value: EXPANDED_COLLAPSE_STATE,
            children: children
          })]
        }), {
          placement: 'end',
          alignment: 'start'
        })]
      });
    }

    // Items without children: icon-only link/button with tooltip
    const collapsedAriaProps = {
      'aria-current': isSelected ? 'page' : undefined,
      'aria-disabled': isDisabled || undefined,
      'aria-label': label,
      'data-testid': testId
    };
    const collapsedElement = /*#__PURE__*/_jsx(NavItemElement, {
      ref: ref,
      href: href,
      as: as,
      isDisabled: isDisabled,
      onClick: handleClick,
      ...collapsedAriaProps,
      ...collapsedItemStyles,
      children: collapsedIcon
    });
    return /*#__PURE__*/_jsxs("div", {
      ref: itemRef,
      ...{
        className: "x78zum5 xdt5ytf xh8yej3"
      },
      children: [collapsedElement, /*#__PURE__*/_jsx(Tooltip, {
        content: label,
        placement: "end",
        anchorRef: itemRef
      })]
    });
  }
  const itemContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [displayIcon && renderIconSlot(displayIcon, {
      size: 'sm',
      color: isSelected ? 'primary' : isDisabled ? 'disabled' : 'secondary'
    }), !isCollapsed && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x98rzlu xeuugli xb3r6kr xlyipyv xuxw1ft"
      },
      children: label
    }), !isCollapsed && endContent && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x2lah0s x78zum5 x6s0dn4"
      },
      children: endContent
    }), !isCollapsed && isItemCollapsible && !hasIndependentToggle && /*#__PURE__*/_jsx("span", {
      ...{
        0: {
          className: "x3nfvp2 x6s0dn4 xl56j7k x1mtvzj0 xvpftlf x11xpdln xuedmi6 xlr8y92 x2lah0s"
        },
        1: {
          className: "x3nfvp2 x6s0dn4 xl56j7k x1mtvzj0 xvpftlf x11xpdln xuedmi6 xlr8y92 x2lah0s x19jd1h0"
        }
      }[!!!isItemCollapsed << 0],
      children: getIcon('chevronDown')
    })]
  });
  const navItemStyleProps = mergeProps(themeProps('side-nav-item', {
    size,
    selected: isSelected ? 'selected' : null
  }), stylex.props(navItemStyles.item, navItemStyles[size], isSelected && navItemStyles.selected, isDisabled && navItemStyles.disabled));

  // ── Split-action path: <div> row with <a> + <button> as siblings ──
  //
  // When both collapsible and href are set, we can't nest a <button>
  // inside an <a>. Instead we use a <div> as the flex row container,
  // an <a display:contents> for the link (with ::before covering the row),
  // and a <button> sibling lifted above the overlay via z-index.

  // ── Split-action path: primary element + toggle button as siblings ──
  //
  // When collapsible and a primary action (href or onClick) are both set,
  // we render a <div> row with the primary element and chevron toggle as
  // siblings. This avoids nesting interactive elements (<button> inside <a>).

  let itemElement;
  if (hasIndependentToggle) {
    itemElement = /*#__PURE__*/_jsxs("div", {
      "data-testid": testId,
      ...navItemStyleProps,
      children: [/*#__PURE__*/_jsx(NavItemElement, {
        ref: ref,
        href: href,
        as: as,
        isDisabled: isDisabled,
        onClick: handleClick,
        "aria-current": isSelected ? 'page' : undefined,
        ...{
          className: "x78zum5 x6s0dn4 x1txdalj x98rzlu xeuugli x1heor9g x1hl2dhg x1717udv x1ghz6dp xc342km xng3xce xjbqb8w xjb2p0i x1qlqyl8 x1pd3egz x15bjb6t x1yc453h x1ypdohk"
        },
        children: itemContent
      }), /*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: handleToggleClick,
        "aria-label": isItemCollapsed ? t('@astryx.sideNavItem.expand', {
          label
        }) : t('@astryx.sideNavItem.collapse', {
          label
        }),
        "aria-expanded": !isItemCollapsed,
        "aria-controls": `${id}-children`,
        ...{
          className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1heor9g x1ypdohk xh6dtrn xe9uy6x xyxi2l3"
        },
        children: /*#__PURE__*/_jsx("span", {
          ...{
            0: {
              className: "x3nfvp2 x6s0dn4 xl56j7k x1mtvzj0 xvpftlf x11xpdln xuedmi6 xlr8y92 x2lah0s"
            },
            1: {
              className: "x3nfvp2 x6s0dn4 xl56j7k x1mtvzj0 xvpftlf x11xpdln xuedmi6 xlr8y92 x2lah0s x19jd1h0"
            }
          }[!!!isItemCollapsed << 0],
          children: getIcon('chevronDown')
        })
      })]
    });
  } else {
    const ariaProps = {
      'aria-current': isSelected ? 'page' : undefined,
      'aria-disabled': isDisabled || undefined,
      'aria-expanded': isItemCollapsible ? !isItemCollapsed : undefined,
      'aria-controls': isItemCollapsible ? `${id}-children` : undefined,
      'data-testid': testId
    };
    itemElement = /*#__PURE__*/_jsx(NavItemElement, {
      ref: ref,
      href: href,
      as: as,
      isDisabled: isDisabled,
      onClick: handleClick,
      ...ariaProps,
      ...navItemStyleProps,
      children: itemContent
    });
  }
  const item = /*#__PURE__*/_jsxs("div", {
    ref: itemRef,
    ...{
      className: "x78zum5 xdt5ytf xh8yej3"
    },
    children: [itemElement, hasChildren && !isCollapsed && /*#__PURE__*/_jsx("div", {
      id: `${id}-children`,
      role: "group",
      "aria-labelledby": `${id}-label`,
      "aria-hidden": isItemCollapsed,
      inert: isItemCollapsed ? true : undefined,
      ...{
        0: {
          className: "xrvj5dj x1tu4anv x1qn9uv2 x80gvsz xlr8y92"
        },
        1: {
          className: "xrvj5dj x1qn9uv2 x80gvsz xlr8y92 xihq33y"
        }
      }[!!isItemCollapsed << 0],
      children: /*#__PURE__*/_jsxs("div", {
        ...{
          className: "xb3r6kr x2lwn1j x31w388"
        },
        children: [/*#__PURE__*/_jsx("span", {
          id: `${id}-label`,
          hidden: true,
          children: label
        }), children]
      })
    })]
  });
  return item;
}
SideNavItem.displayName = 'SideNavItem';